import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  checkLoginStatus, 
  fetchBinaryTree, 
  fetchBinaryTreeNodeChildren 
} from '../../../redux/WithcookieSlice';
import { useTranslation } from '../../../redux/TranslationHelper';
import { OrganizationChartNodeData, BinaryLegsLogicReturn } from './BinaryLegs.types';


export const useBinaryLegsLogic = (props: any): BinaryLegsLogicReturn => {
  const dispatch = useDispatch() as any;
  const { t } = useTranslation();
  
  // Redux state
  const user = useSelector((state: any) => state.withcookie.user);
  const binaryTreeData = useSelector((state: any) => state.withcookie.binaryTreeData);
  const binaryTreeLoading = useSelector((state: any) => state.withcookie.binaryTreeLoading);
  const binaryTreeError = useSelector((state: any) => state.withcookie.binaryTreeError);
  const withcookieData = useSelector((state: any) => state.withcookie.withcookieData);
  
  // Theme colors
  const theme = withcookieData?.skin?.theme || {};
  const colorSet = theme?.colorset || {};
  const primaryColor = colorSet?.primary || "#3f51b5";
  const secondaryColor = colorSet?.secondary || "#e91e63";
  
  // Component state
  const [currentUserId, setCurrentUserId] = useState<string | number | null>(null);
  const [debugMode, setDebugMode] = useState<boolean>(
    typeof props.showDebugMode !== 'undefined' ? props.showDebugMode : false
  );
  const [mockingEnabled, setMockingEnabled] = useState<boolean>(false);
  const [customApiUrl, setCustomApiUrl] = useState<string>('');
  const [nodeHistory, setNodeHistory] = useState<Array<string | number>>([]);
  const [zoomLevel, setZoomLevel] = useState<number>(props.initialZoom || 1);
  const [offset, setOffset] = useState<{ x: number, y: number }>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [loadingNodes, setLoadingNodes] = useState<Set<string | number>>(new Set());
  
  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const lastPos = useRef<{ x: number, y: number }>({ x: 0, y: 0 });
  const lastTouchDist = useRef<number | null>(null);
  const fetchInProgressRef = useRef(false);
  const lastFetchParamsRef = useRef<string>('');
  const mountedRef = useRef(true);
  
  const minZoom = props.minZoom || 0.3;
  const maxZoom = props.maxZoom || 2.5;
  
  // Component unmount detection
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      fetchInProgressRef.current = false;
    };
  }, []);
  
  // Center chart function
  const centerChart = useCallback(() => {
    if (!containerRef.current || hasUserInteracted) return;
    
    const container = containerRef.current;
    const orgChart = container.querySelector('.orgchart') as HTMLElement;
    
    if (!orgChart) return;
    
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    
    const chartRect = orgChart.getBoundingClientRect();
    const chartWidth = chartRect.width;
    const chartHeight = chartRect.height;
    
    const centerX = containerWidth / 2;
    const centerY = containerHeight / 2;
    
    const chartCenterX = chartWidth / 2;
    const offsetX = centerX - chartCenterX;
    
    let offsetY;
    if (chartHeight > containerHeight * 0.9) {
      offsetY = 30;
    } else {
      offsetY = containerHeight * 0.25;
    }
    
    setOffset({
      x: offsetX,
      y: offsetY
    });
    
    setIsInitialLoad(false);
  }, [hasUserInteracted]);
  
  // Zoom at point function
  const zoomAtPoint = useCallback((newZoom: number, clientX: number, clientY: number) => {
    if (!containerRef.current || newZoom === zoomLevel) return;
    
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    
    const mouseX = clientX - rect.left;
    const mouseY = clientY - rect.top;
    
    const scale = newZoom / zoomLevel;
    
    const newOffsetX = mouseX - (mouseX - offset.x) * scale;
    const newOffsetY = mouseY - (mouseY - offset.y) * scale;
    
    setZoomLevel(newZoom);
    setOffset({
      x: newOffsetX,
      y: newOffsetY
    });
  }, [zoomLevel, offset]);
  
  // Generate mock data
  const generateMockData = useCallback((rootUserId: string | number): OrganizationChartNodeData => {
    const rootId = rootUserId || (user?.id || 1);
    return {
      id: rootId,
      name: `사용자 ${rootId}`,
      userId: `user_${rootId}`,
      phone: '010-1234-5678',
      center: '테스트 센터',
      class: 'shipper',
      backgroundColor: '#6D81F8',
      totalPV: 500,
      rank: '3PS',
      grade: 'Gold',
      groupPv: 1500,
      underMore: true,
      children: [
        {
          id: Number(rootId) + 1,
          name: `사용자 ${Number(rootId) + 1}`,
          userId: `user_${Number(rootId) + 1}`,
          phone: '010-2345-6789',
          center: '왼쪽 센터',
          class: '',
          backgroundColor: '#BAF855',
          totalPV: 1000,
          rank: '2PS',
          grade: 'Silver',
          groupPv: 3000,
          underMore: true,
          children: []
        },
        {
          id: Number(rootId) + 2,
          name: `사용자 ${Number(rootId) + 2}`,
          userId: `user_${Number(rootId) + 2}`,
          phone: '010-3456-7890',
          center: '오른쪽 센터',
          class: '',
          backgroundColor: '#FF6B6B',
          totalPV: undefined,
          rank: '1PS',
          grade: 'Bronze',
          groupPv: 800,
          underMore: false,
          children: []
        }
      ]
    };
  }, [user]);
  
  // Node click handler
  const handleNodeClick = useCallback((nodeData: OrganizationChartNodeData, isRootNode = false) => {
    if (String(currentUserId) === String(nodeData.id) && !isRootNode) {
      return;
    }
    
    if (currentUserId !== null && String(currentUserId) !== String(nodeData.id)) {
      setNodeHistory(prev => [...prev, currentUserId]);
    }
    
    if (String(currentUserId) !== String(nodeData.id)) {
      setCurrentUserId(nodeData.id);
    }
    
    const apiParams = {
      id: String(nodeData.id),
      depth: "6"
    };
    
    const isTopLevelNode = isRootNode || 
                           (binaryTreeData && String(binaryTreeData.id) === String(nodeData.id)) ||
                           !binaryTreeData;
    
    if (isTopLevelNode || isRootNode) {
      (apiParams as any).one_upper = true;
    }
    
    dispatch(fetchBinaryTree({...apiParams}));
  }, [dispatch, currentUserId, binaryTreeData]);
  
  // Go to previous node
  const goToPreviousNode = useCallback(() => {
    if (!nodeHistory || nodeHistory.length === 0) {
      return;
    }
    
    const prevNodeId = nodeHistory[nodeHistory.length - 1];
    setNodeHistory(nodeHistory.slice(0, -1));
    setCurrentUserId(prevNodeId);
    
    const isUserRootNode = user && user.id && String(prevNodeId) === String(user.id);
    
    const apiParams = {
      id: String(prevNodeId),
      depth: "8"
    };
    
    if (isUserRootNode) {
      (apiParams as any).one_upper = true;
    }
    
    dispatch(fetchBinaryTree({...apiParams}));
  }, [nodeHistory, dispatch, user]);
  
  // Go to root node
  const goToRootNode = useCallback(() => {
    if (!user || !user.id) {
      return;
    }
    
    if (String(currentUserId) === String(user.id)) {
      return;
    }
    
    setNodeHistory([]);
    setCurrentUserId(user.id);
    
    const apiParams = {
      id: String(user.id),
      depth: "8",
      one_upper: true
    };
    
    dispatch(fetchBinaryTree({...apiParams}));
  }, [user, dispatch, currentUserId]);
  
  // Toggle debug mode
  const toggleDebugMode = useCallback(() => {
    setDebugMode(!debugMode);
  }, [debugMode]);
  
  // Enable mock data
  const enableMockData = useCallback(() => {
    setMockingEnabled(true);
  }, []);
  
  // Retry with real API
  const retryWithRealApi = useCallback(() => {
    if (user && user.id) {
      setMockingEnabled(false);
      dispatch(fetchBinaryTree({ id: String(user.id) }));
    }
  }, [user, dispatch]);
  
  // Mouse wheel handler
  const handleWheel = useCallback((e: React.WheelEvent) => {
    setHasUserInteracted(true);
    e.preventDefault();
    e.stopPropagation();
    
    const zoomSpeed = 0.15;
    let delta = e.deltaY < 0 ? zoomSpeed : -zoomSpeed;
    
    if (e.ctrlKey) {
      delta *= 0.5;
    }
    
    const newZoom = Math.max(minZoom, Math.min(maxZoom, zoomLevel + delta));
    
    if (newZoom !== zoomLevel) {
      zoomAtPoint(newZoom, e.clientX, e.clientY);
    }
  }, [zoomLevel, zoomAtPoint, minZoom, maxZoom]);
  
  // Mouse drag handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setHasUserInteracted(true);
    
    if (e.button === 0) {
      setIsDragging(true);
      lastPos.current = { x: e.clientX, y: e.clientY };
      
      const container = e.currentTarget as HTMLElement;
      if (container) {
        container.style.cursor = 'grabbing';
      }
      
      e.preventDefault();
    }
  }, []);
  
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    
    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;
    
    setOffset(prev => ({
      x: prev.x + dx,
      y: prev.y + dy
    }));
    
    lastPos.current = { x: e.clientX, y: e.clientY };
  }, [isDragging]);
  
  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      setIsDragging(false);
      
      const container = e.currentTarget as HTMLElement;
      if (container) {
        container.style.cursor = 'grab';
      }
    }
  }, [isDragging]);
  
  // Touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setHasUserInteracted(true);
    e.preventDefault();
    
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastTouchDist.current = Math.sqrt(dx * dx + dy * dy);
      
      const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      lastPos.current = { x: centerX, y: centerY };
      
      setIsDragging(false);
    } else if (e.touches.length === 1) {
      setIsDragging(true);
      lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  }, []);
  
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    
    if (e.touches.length === 2 && lastTouchDist.current) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      
      const dx = touch1.clientX - touch2.clientX;
      const dy = touch1.clientY - touch2.clientY;
      const newDist = Math.sqrt(dx * dx + dy * dy);
      
      if (Math.abs(newDist - lastTouchDist.current) > 5) {
        const centerX = (touch1.clientX + touch2.clientX) / 2;
        const centerY = (touch1.clientY + touch2.clientY) / 2;
        
        const scaleFactor = newDist / lastTouchDist.current;
        const clampedScale = Math.max(0.9, Math.min(1.1, scaleFactor));
        const newZoom = Math.min(maxZoom, Math.max(minZoom, zoomLevel * clampedScale));
        
        if (newZoom !== zoomLevel) {
          zoomAtPoint(newZoom, centerX, centerY);
        }
        
        lastTouchDist.current = newDist;
      }
    } else if (e.touches.length === 1 && isDragging) {
      const dx = e.touches[0].clientX - lastPos.current.x;
      const dy = e.touches[0].clientY - lastPos.current.y;
      
      setOffset(prev => ({
        x: prev.x + dx,
        y: prev.y + dy
      }));
      
      lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  }, [isDragging, zoomLevel, zoomAtPoint, minZoom, maxZoom]);
  
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    lastTouchDist.current = null;
    
    if (e.touches.length === 0) {
      setIsDragging(false);
    }
  }, []);
  
  // Manual center chart
  const manualCenterChart = useCallback(() => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const orgChart = container.querySelector('.orgchart') as HTMLElement;
    
    if (!orgChart) return;
    
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    
    const chartRect = orgChart.getBoundingClientRect();
    const chartWidth = chartRect.width;
    
    const centerX = containerWidth / 2;
    const chartCenterX = chartWidth / 2;
    const offsetX = centerX - chartCenterX;
    
    let offsetY;
    if (chartRect.height > containerHeight * 0.9) {
      offsetY = 30;
    } else {
      offsetY = containerHeight * 0.25;
    }
    
    setOffset({
      x: offsetX,
      y: offsetY
    });
  }, []);
  
  // Reset zoom
  const resetZoom = useCallback(() => {
    setZoomLevel(1);
    setHasUserInteracted(false);
    setTimeout(() => {
      manualCenterChart();
    }, 50);
  }, [manualCenterChart]);
  
  // Handle more button click
  const handleMoreButtonClick = useCallback(async (nodeId: string | number) => {
    setLoadingNodes(prev => new Set(prev).add(nodeId));
    
    try {
      await dispatch(fetchBinaryTreeNodeChildren({
        id: String(nodeId),
        depth: "8"
      })).unwrap();
    } catch (error) {
      console.error('[바이너리 레그] 추가 데이터 로드 오류:', error);
    } finally {
      setLoadingNodes(prev => {
        const newSet = new Set(prev);
        newSet.delete(nodeId);
        return newSet;
      });
    }
  }, [dispatch]);
  
  // Initial data load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        await dispatch(checkLoginStatus({ forceRefresh: true }));
      } finally {
        if (mountedRef.current) {
          fetchInProgressRef.current = false;
        }
      }
    };
    
    checkAuth();
  }, [dispatch]);
  
  // Load binary tree when user is available
  useEffect(() => {
    if (user && !binaryTreeData && !binaryTreeLoading) {
      if (currentUserId === null) {
        setCurrentUserId(user.id);
        setNodeHistory([]);
        
        dispatch(fetchBinaryTree({ 
          id: String(user.id),
          depth: "8",
          one_upper: true 
        }));
      }
    }
  }, [user, dispatch, binaryTreeData, binaryTreeLoading, currentUserId]);
  
  // Center chart on initial load
  useEffect(() => {
    if (binaryTreeData && !binaryTreeLoading && isInitialLoad && !hasUserInteracted) {
      const timer = setTimeout(() => {
        centerChart();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [binaryTreeData, binaryTreeLoading, isInitialLoad, hasUserInteracted, centerChart]);
  
  // Reset user interaction on node change
  useEffect(() => {
    if (currentUserId !== null && binaryTreeData && !binaryTreeLoading) {
      setHasUserInteracted(false);
      setIsInitialLoad(true);
    }
  }, [currentUserId, binaryTreeData, binaryTreeLoading]);
  
  return {
    // State
    currentUserId,
    debugMode,
    mockingEnabled,
    customApiUrl,
    nodeHistory,
    zoomLevel,
    offset,
    isDragging,
    hasUserInteracted,
    isInitialLoad,
    loadingNodes,
    
    // Redux state
    user,
    binaryTreeData,
    binaryTreeLoading,
    binaryTreeError,
    withcookieData,
    primaryColor,
    secondaryColor,
    
    // Refs
    containerRef,
    lastPos,
    lastTouchDist,
    
    // Actions
    handleNodeClick,
    handleMoreButtonClick,
    goToPreviousNode,
    goToRootNode,
    toggleDebugMode,
    enableMockData,
    retryWithRealApi,
    handleWheel,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    manualCenterChart,
    resetZoom,
    setDebugMode,
    setMockingEnabled,
    setCustomApiUrl,
    
    // Utils
    t,
    generateMockData
  };
};